import React, { useEffect } from 'react';

const MessageQueues = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Message Queues: Kafka, RabbitMQ, SQS - Complete Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master message queues for scalable systems. Learn Kafka, RabbitMQ, SQS patterns, async communication, and event-driven architectures with real examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master message queues for scalable systems. Learn Kafka, RabbitMQ, SQS patterns, async communication, and event-driven architectures with real examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Message Queues in Distributed Systems</h1>
        <p style={styles.subtitle}>
          Build scalable, decoupled systems with Kafka, RabbitMQ, SQS, and asynchronous messaging patterns
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What are Message Queues?</h2>
          <p style={styles.paragraph}>
            Message queues are communication mechanisms that enable asynchronous message passing between distributed 
            systems. They act as intermediaries that store messages until consuming applications are ready to process them, 
            enabling loose coupling and improved scalability.
          </p>
          <p style={styles.paragraph}>
            Message queues enable systems to handle traffic spikes, process work asynchronously, and maintain availability 
            even when individual components fail. They're essential for building resilient, scalable distributed systems 
            that can handle millions of messages per second.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Core Message Queue Concepts</h2>
          
          <h3 style={styles.heading}>Producer-Consumer Pattern</h3>
          <p style={styles.paragraph}>
            The fundamental pattern involves producers (publishers) sending messages to queues, and consumers (subscribers) 
            processing messages from queues. This decoupling allows producers and consumers to operate independently, 
            scaling and failing independently.
          </p>
          <pre style={styles.codeBlock}>
{`Producer → Queue → Consumer

Benefits:
✓ Decoupling: Producer and consumer don't need to know about each other
✓ Scalability: Multiple producers and consumers can work independently  
✓ Reliability: Messages persist even if consumer is temporarily unavailable
✓ Load Leveling: Queues absorb traffic spikes`}
          </pre>

          <h3 style={styles.heading}>Message Delivery Patterns</h3>
          <p style={styles.paragraph}>
            <strong>Point-to-Point:</strong> One message is consumed by exactly one consumer. Used for task distribution 
            and work queues where each task should be processed once.
          </p>
          <p style={styles.paragraph}>
            <strong>Publish-Subscribe:</strong> One message is delivered to multiple subscribers. Used for event 
            notifications and broadcasting information to multiple interested parties.
          </p>
          <p style={styles.paragraph}>
            <strong>Request-Response:</strong> Synchronous-like communication over asynchronous messaging using reply 
            queues and correlation IDs. Used for RPC-style communication over messaging infrastructure.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Popular Message Queue Systems</h2>
          
          <h3 style={styles.heading}>Apache Kafka</h3>
          <p style={styles.paragraph}>
            Kafka is a high-throughput, distributed streaming platform designed for real-time data feeds and event streaming. 
            It can handle millions of messages per second and provides strong durability guarantees with configurable 
            replication and retention policies.
          </p>
          <pre style={styles.codeBlock}>
{`// Kafka Producer Example (Java)
Properties props = new Properties();
props.put("bootstrap.servers", "localhost:9092");
props.put("key.serializer", "org.apache.kafka.common.serialization.StringSerializer");
props.put("value.serializer", "org.apache.kafka.common.serialization.StringSerializer");

Producer<String, String> producer = new KafkaProducer<>(props);

// Send message
ProducerRecord<String, String> record = 
    new ProducerRecord<>("user-events", "user123", "login");
producer.send(record, (metadata, exception) -> {
    if (exception != null) {
        exception.printStackTrace();
    } else {
        System.out.println("Message sent to partition " + metadata.partition());
    }
});

// Kafka Consumer Example
Properties consumerProps = new Properties();
consumerProps.put("bootstrap.servers", "localhost:9092");
consumerProps.put("group.id", "user-service");
consumerProps.put("key.deserializer", "org.apache.kafka.common.serialization.StringDeserializer");
consumerProps.put("value.deserializer", "org.apache.kafka.common.serialization.StringDeserializer");

KafkaConsumer<String, String> consumer = new KafkaConsumer<>(consumerProps);
consumer.subscribe(Arrays.asList("user-events"));

while (true) {
    ConsumerRecords<String, String> records = consumer.poll(Duration.ofMillis(100));
    for (ConsumerRecord<String, String> record : records) {
        System.out.printf("Received: key=%s, value=%s, partition=%d%n",
            record.key(), record.value(), record.partition());
    }
}`}
          </pre>
          <p style={styles.paragraph}>
            <strong>Kafka Strengths:</strong> Extremely high throughput (millions of messages/sec), horizontal scaling, 
            message persistence, real-time streaming capabilities, and strong ordering guarantees within partitions.
          </p>

          <h3 style={styles.heading}>RabbitMQ</h3>
          <p style={styles.paragraph}>
            RabbitMQ is a feature-rich message broker with flexible routing, multiple messaging patterns, and strong 
            consistency guarantees. It supports various protocols (AMQP, MQTT, STOMP) and provides a management UI 
            for monitoring and administration.
          </p>
          <pre style={styles.codeBlock}>
{`// RabbitMQ Producer Example (Python)
import pika
import json

# Connection
connection = pika.BlockingConnection(
    pika.ConnectionParameters('localhost')
)
channel = connection.channel()

# Declare queue
channel.queue_declare(queue='task_queue', durable=True)

# Send message
message = {
    "user_id": "123",
    "action": "purchase",
    "amount": 99.99
}

channel.basic_publish(
    exchange='',
    routing_key='task_queue',
    body=json.dumps(message),
    properties=pika.BasicProperties(
        delivery_mode=2,  # Make message persistent
    )
)
connection.close()

# RabbitMQ Consumer Example
def callback(ch, method, properties, body):
    message = json.loads(body)
    print(f"Processing: {message}")
    
    # Simulate work
    time.sleep(2)
    
    # Acknowledge message
    ch.basic_ack(delivery_tag=method.delivery_tag)

channel.basic_qos(prefetch_count=1)  # Fair dispatch
channel.basic_consume(queue='task_queue', on_message_callback=callback)
channel.start_consuming()`}
          </pre>
          <p style={styles.paragraph}>
            <strong>RabbitMQ Strengths:</strong> Flexible routing patterns, message acknowledgments, clustering support, 
            management UI, multiple protocols (AMQP, MQTT, STOMP), and strong consistency guarantees.
          </p>

          <h3 style={styles.heading}>Amazon SQS</h3>
          <p style={styles.paragraph}>
            SQS is a fully managed message queuing service with built-in scaling, security, and reliability features. 
            It integrates seamlessly with other AWS services and provides both standard and FIFO queue options.
          </p>
          <pre style={styles.codeBlock}>
{`// AWS SQS Example (Python boto3)
import boto3
import json

# Create SQS client
sqs = boto3.client('sqs', region_name='us-east-1')
queue_url = 'https://sqs.us-east-1.amazonaws.com/123456789/my-queue'

# Send message
message_body = {
    "orderId": "12345",
    "customerId": "67890",
    "items": ["item1", "item2"]
}

response = sqs.send_message(
    QueueUrl=queue_url,
    MessageBody=json.dumps(message_body),
    MessageAttributes={
        'Priority': {
            'StringValue': 'High',
            'DataType': 'String'
        }
    }
)

# Receive and process messages
def process_messages():
    while True:
        response = sqs.receive_message(
            QueueUrl=queue_url,
            MaxNumberOfMessages=10,
            WaitTimeSeconds=20  # Long polling
        )
        
        messages = response.get('Messages', [])
        for message in messages:
            # Process message
            body = json.loads(message['Body'])
            print(f"Processing order: {body['orderId']}")
            
            # Delete message after processing
            sqs.delete_message(
                QueueUrl=queue_url,
                ReceiptHandle=message['ReceiptHandle']
            )`}
          </pre>
          <p style={styles.paragraph}>
            <strong>SQS Strengths:</strong> Fully managed, auto-scaling, integrated with AWS ecosystem, dead letter 
            queues, FIFO queues available, and built-in security features.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Common Messaging Patterns</h2>
          
          <h3 style={styles.heading}>Work Queue Pattern</h3>
          <p style={styles.paragraph}>
            The work queue pattern distributes time-consuming tasks among multiple workers for parallel processing. 
            This pattern is ideal for CPU-intensive tasks, image processing, data analysis, or any work that can be 
            parallelized across multiple workers.
          </p>
          <pre style={styles.codeBlock}>
{`class TaskProcessor:
    def __init__(self, queue_name):
        self.queue = MessageQueue(queue_name)
    
    def submit_task(self, task_data):
        """Submit task to work queue"""
        message = {
            'task_id': str(uuid.uuid4()),
            'task_type': task_data['type'],
            'payload': task_data['payload'],
            'created_at': datetime.utcnow().isoformat()
        }
        self.queue.send(message)
        return message['task_id']
    
    def process_tasks(self):
        """Worker process to handle tasks"""
        while True:
            message = self.queue.receive(timeout=30)
            if message:
                try:
                    self.handle_task(message)
                    self.queue.acknowledge(message)
                except Exception as e:
                    print(f"Task failed: {e}")
                    self.queue.reject(message, requeue=True)

# Usage
processor = TaskProcessor('image-processing-queue')

# Submit tasks
processor.submit_task({
    'type': 'resize_image',
    'payload': {'image_url': 'http://example.com/image.jpg', 'size': '300x300'}
})

# Multiple workers can process tasks in parallel
processor.process_tasks()`}
          </pre>

          <h3 style={styles.heading}>Event-Driven Architecture</h3>
          <p style={styles.paragraph}>
            In event-driven architecture, services publish events when state changes occur, enabling loose coupling 
            and reactive systems. This pattern is excellent for microservices architectures where services need to 
            react to changes in other services without tight coupling.
          </p>
          <pre style={styles.codeBlock}>
{`class EventPublisher:
    def __init__(self, exchange_name):
        self.exchange = MessageExchange(exchange_name)
    
    def publish_event(self, event_type, data):
        event = {
            'event_id': str(uuid.uuid4()),
            'event_type': event_type,
            'timestamp': datetime.utcnow().isoformat(),
            'data': data
        }
        self.exchange.publish(event_type, event)

class EventSubscriber:
    def __init__(self, exchange_name, service_name):
        self.exchange = MessageExchange(exchange_name)
        self.queue_name = f"{service_name}-events"
        
    def subscribe_to_events(self, event_types, callback):
        for event_type in event_types:
            self.exchange.bind_queue(self.queue_name, event_type)
        
        while True:
            message = self.exchange.receive(self.queue_name)
            if message:
                callback(message)

# Usage Example: E-commerce system
publisher = EventPublisher('ecommerce-events')

# Order service publishes events
def create_order(order_data):
    order = save_order(order_data)
    publisher.publish_event('order.created', {
        'order_id': order.id,
        'customer_id': order.customer_id,
        'total': order.total
    })

# Inventory service subscribes to order events
inventory_subscriber = EventSubscriber('ecommerce-events', 'inventory-service')

def handle_order_event(event):
    if event['event_type'] == 'order.created':
        update_inventory(event['data']['order_id'])

inventory_subscriber.subscribe_to_events(['order.created'], handle_order_event)`}
          </pre>

          <h3 style={styles.heading}>Request-Response with Correlation ID</h3>
          <p style={styles.paragraph}>
            This pattern implements synchronous-like communication over asynchronous messaging using correlation IDs 
            and reply queues. It's useful when you need RPC-style communication but want the benefits of message queues 
            like durability and load balancing.
          </p>
          <pre style={styles.codeBlock}>
{`class RPCClient:
    def __init__(self):
        self.request_queue = 'rpc_requests'
        self.response_queue = 'rpc_responses'
        self.pending_requests = {}
    
    def call(self, method, params, timeout=30):
        correlation_id = str(uuid.uuid4())
        
        request = {
            'method': method,
            'params': params,
            'correlation_id': correlation_id,
            'reply_to': self.response_queue
        }
        
        # Store callback for response
        future = asyncio.Future()
        self.pending_requests[correlation_id] = future
        
        # Send request
        self.send_message(self.request_queue, request)
        
        # Wait for response
        try:
            response = await asyncio.wait_for(future, timeout=timeout)
            return response
        finally:
            self.pending_requests.pop(correlation_id, None)
    
    def handle_response(self, message):
        correlation_id = message.get('correlation_id')
        if correlation_id in self.pending_requests:
            future = self.pending_requests[correlation_id]
            future.set_result(message.get('result'))

class RPCServer:
    def __init__(self):
        self.request_queue = 'rpc_requests'
        self.methods = {}
    
    def register_method(self, name, handler):
        self.methods[name] = handler
    
    def process_requests(self):
        while True:
            message = self.receive_message(self.request_queue)
            if message:
                try:
                    method = message['method']
                    params = message['params']
                    result = self.methods[method](params)
                    
                    response = {
                        'correlation_id': message['correlation_id'],
                        'result': result
                    }
                    
                    self.send_message(message['reply_to'], response)
                except Exception as e:
                    error_response = {
                        'correlation_id': message['correlation_id'],
                        'error': str(e)
                    }
                    self.send_message(message['reply_to'], error_response)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Idempotent message processing:</strong> Design consumers to handle duplicate messages gracefully 
              since message queues often provide at-least-once delivery guarantees.
            </li>
            <li style={styles.listItem}>
              <strong>Dead letter queues:</strong> Implement dead letter queues for messages that fail processing 
              repeatedly to prevent them from blocking the main queue.
            </li>
            <li style={styles.listItem}>
              <strong>Message versioning:</strong> Include version information in messages to handle schema evolution 
              and maintain backward compatibility.
            </li>
            <li style={styles.listItem}>
              <strong>Monitoring and alerting:</strong> Monitor queue depth, processing rates, error rates, and 
              consumer lag to detect issues early.
            </li>
            <li style={styles.listItem}>
              <strong>Backpressure handling:</strong> Implement mechanisms to handle slow consumers and prevent 
              queue buildup that could lead to memory issues.
            </li>
            <li style={styles.listItem}>
              <strong>Security:</strong> Use authentication, authorization, and encryption for message transport, 
              especially when dealing with sensitive data.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Message queues are essential for building scalable, resilient distributed systems. They enable asynchronous 
            communication, decouple components, and provide reliability through message persistence and delivery guarantees.
          </p>
          <p style={styles.paragraph}>
            Choose your message queue system based on your specific requirements: Kafka for high-throughput streaming, 
            RabbitMQ for flexible routing and reliability, or SQS for managed simplicity in AWS environments. The key 
            is understanding your use case and selecting the right tool for the job.
          </p>
        </section>
      </main>
    </article>
  );
};

export default MessageQueues;